import React, { useRef, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { fg } from '@confluence/feature-gating';

import { GroupDetails } from './shareAndRestrictDialog/body/specificAccessDetails/specificAccessRow/GroupDetails';
import type { GroupOrTeamPrincipal } from './commonTypes';

export type GroupNotifyWarningDialogProps = {
	groupNames: string[];
	groupList: GroupOrTeamPrincipal[];
	isSaving: boolean;
	onDoNotify: () => void;
	onDoNotNotify: () => void;
	onCloseComplete: () => void;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.group-notify-warning.heading',
		defaultMessage: 'Notify everyone in the group?',
		description:
			'Heading for a pop-up modal that asks a user to confirm sending notifications to all group members',
	},
	headingWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.heading-with-teams',
		defaultMessage: 'Notify everyone in the teams and groups?',
		description:
			'Heading for a pop-up modal that asks a user to confirm sending notifications to all team and group members',
	},
	body: {
		id: 'share-and-restrict-dialog.group-notify-warning.body',
		defaultMessage:
			'Groups often contain a lot of people. It might be a little noisy to notify them all. Are you sure you want to notify everyone in these groups?',
		description: 'Body for a pop-up modal that interrupts sharing content to groups',
	},
	bodyWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.body-with-teams',
		defaultMessage:
			'Teams and groups often contain a lot of people. It might be a little noisy to notify them all. Are you sure you want to notify in these teams and groups?',
		description: 'Body for a pop-up modal that interrupts sharing content to teams and groups',
	},
	doNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-notify',
		defaultMessage: 'Notify groups',
		description:
			'The label for a button that will share a document and notify all new users, including those in groups',
	},
	doNotifyButtonWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-notify-with-teams',
		defaultMessage: 'Notify them all',
		description:
			'The label for a button that will share a document and notify all new users, including those in groups',
	},
	doNotNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-not-notify',
		defaultMessage: 'Share without notifying',
		description:
			'The label for a button that will share a document, and notify individuals but not group members',
	},
});

export const GroupNotifyWarningDialog: FC<GroupNotifyWarningDialogProps> = ({
	groupNames,
	groupList,
	isSaving,
	onDoNotify,
	onDoNotNotify,
	onCloseComplete,
}) => {
	const focusRef = useRef<HTMLButtonElement>(null);

	return (
		<ModalDialog
			width="medium"
			autoFocus={focusRef}
			onCloseComplete={onCloseComplete}
			testId="modal-dialog-share-group-notify-warning"
		>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage
						{...(fg('confluence_frontend_perms_exp_teams_as_principals')
							? i18n.headingWithTeams
							: i18n.heading)}
					/>
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<FormattedMessage
						{...(fg('confluence_frontend_perms_exp_teams_as_principals')
							? i18n.bodyWithTeams
							: i18n.body)}
					/>
					{!fg('confluence_frontend_perms_exp_teams_as_principals') ? (
						<Inline>
							<Text weight="bold">{groupNames.join(', ')}</Text>
						</Inline>
					) : (
						<Stack space="space.200">
							{groupList.map((group) => {
								return <GroupDetails key={group.id} {...group} />;
							})}
						</Stack>
					)}
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button isDisabled={isSaving} onClick={onDoNotNotify}>
					<FormattedMessage {...i18n.doNotNotifyButton} />
				</Button>
				<Button appearance="primary" isDisabled={isSaving} onClick={onDoNotify} ref={focusRef}>
					<FormattedMessage
						{...(fg('confluence_frontend_perms_exp_teams_as_principals')
							? i18n.doNotifyButtonWithTeams
							: i18n.doNotifyButton)}
					/>
				</Button>
			</ModalFooter>
		</ModalDialog>
	);
};
